/**
 * Attachments widget factory — owns API→UI mapping and edit-callback wiring.
 * Re-entry detail files only register or invoke this factory.
 *
 * `config.attachmentIds` orders hydrated rows. Commit and unlink own that list,
 * so it is never written through the widget config PATCH.
 */

import { ATTACHMENTS_WIDGET_TYPE } from '../../constants/reentry-planning.constants';
import { isPlainObject } from '../../utils/plain-object.util';
import { ATTACHMENT_ACCEPT, ATTACHMENT_ITEM_STATUS } from './attachment.constants';

const MAX_ATTACHMENTS_METADATA_KEY = 'max_attachments';

export const isAttachmentsWidget = (widget) => widget?.widgetType === ATTACHMENTS_WIDGET_TYPE;

const readAttachmentFileName = (attachment) => {
  if (typeof attachment?.filename === 'string') {
    return attachment.filename;
  }
  if (typeof attachment?.fileName === 'string') {
    return attachment.fileName;
  }
  return '';
};

/** One hydrated API row → WidgetBody item. Client status is always success. */
export const mapAttachmentItem = (attachment) => ({
  id: attachment?.id == null ? '' : String(attachment.id),
  fileName: readAttachmentFileName(attachment),
  mimeType: typeof attachment?.mimeType === 'string' ? attachment.mimeType : '',
  status: ATTACHMENT_ITEM_STATUS.SUCCESS,
});

const readOrderedAttachments = (widget) => {
  const attachments = Array.isArray(widget?.attachments) ? widget.attachments : [];
  const config = isPlainObject(widget?.config) ? widget.config : {};
  const ids = Array.isArray(config.attachmentIds) ? config.attachmentIds : null;

  if (!ids) {
    return attachments;
  }

  const byId = new Map(attachments.map((attachment) => [attachment?.id, attachment]));
  return ids.map((id) => byId.get(id)).filter(Boolean);
};

/** Numeric `metadata.max_attachments` caps the add control. Null or absent means unlimited. */
export const readMaxFiles = (metadata) => {
  if (!isPlainObject(metadata)) {
    return undefined;
  }

  const value = metadata[MAX_ATTACHMENTS_METADATA_KEY];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

/**
 * Maps a hydrated attachments widget to AttachmentsWidgetBody props.
 * Defaults to readOnly; editable views attach callbacks via attachEditCallbacks.
 */
export const mapAttachmentsWidgetProps = (widget, _reentryPlanItems = {}, options = {}) => {
  const maxFiles = readMaxFiles(widget?.metadata);
  const readOnly = options.readOnly !== false;

  return {
    items: readOrderedAttachments(widget).map(mapAttachmentItem),
    readOnly,
    accept: ATTACHMENT_ACCEPT,
    ...(maxFiles === undefined ? {} : { maxFiles }),
  };
};

/**
 * Attaches open, and when mutation handlers are present, add/remove.
 * Open-only handlers leave `readOnly` as mapped so archived and view-only plans
 * can still open files.
 *
 * @param {object} widget - Mapped UI widget (`type` / `id` / `props`)
 * @param {object} handlers - Handlers from useAttachmentWidgetUpload
 */
export const attachAttachmentCallbacks = (widget, handlers) => {
  const props = {
    ...widget.props,
    onOpen: (attachmentId) => handlers.onOpen(widget.id, attachmentId),
  };

  if (typeof handlers.onAddFiles === 'function') {
    props.readOnly = false;
    props.onAddFiles = (files) => handlers.onAddFiles(widget.id, files);
    props.onRemove = (attachmentId) => handlers.onRemove(widget.id, attachmentId);
  }

  return {
    ...widget,
    props,
  };
};

/** Replaces attachment items for one widget id (upload progress and optimistic remove). */
export const replaceAttachmentWidgetItems = (sections = [], widgetId, items) =>
  sections.map((section) => ({
    ...section,
    widgets: (section.widgets ?? []).map((widget) => {
      if (widget.id !== widgetId || widget.type !== ATTACHMENTS_WIDGET_TYPE) {
        return widget;
      }
      return {
        ...widget,
        props: {
          ...widget.props,
          items,
        },
      };
    }),
  }));

/** Swaps one mapped attachments widget after commit. */
export const replaceAttachmentWidget = (sections = [], widgetId, nextWidget) =>
  sections.map((section) => ({
    ...section,
    widgets: (section.widgets ?? []).map((widget) =>
      widget.id === widgetId && widget.type === ATTACHMENTS_WIDGET_TYPE ? nextWidget : widget,
    ),
  }));

const toHydrationRecord = (item) => ({
  id: item.id,
  filename: item.filename ?? item.fileName ?? '',
  mimeType: item.mimeType ?? '',
  fileSize: typeof item.fileSize === 'number' ? item.fileSize : 0,
});

/**
 * Commit returns the widget row, which may not include hydrated `attachments`.
 * When that array is absent, rebuild it in `config.attachmentIds` order from the
 * chips we already showed plus the files just committed.
 */
export const hydrateCommittedWidget = (response, previousItems = [], committed = []) => {
  if (!response || typeof response !== 'object' || Array.isArray(response.attachments)) {
    return response;
  }

  const byId = new Map();
  previousItems
    .filter((item) => item?.status === ATTACHMENT_ITEM_STATUS.SUCCESS && item.id)
    .forEach((item) => byId.set(item.id, toHydrationRecord(item)));
  committed.forEach((item) => {
    if (item?.id) {
      byId.set(item.id, toHydrationRecord(item));
    }
  });

  const config = isPlainObject(response.config) ? response.config : {};
  const ids = Array.isArray(config.attachmentIds) ? config.attachmentIds : [...byId.keys()];

  return {
    ...response,
    attachments: ids.map((id) => byId.get(id)).filter(Boolean),
  };
};

export const attachmentsWidgetFactory = {
  matches: isAttachmentsWidget,
  type: ATTACHMENTS_WIDGET_TYPE,
  mapProps: mapAttachmentsWidgetProps,
  attachEditCallbacks: attachAttachmentCallbacks,
};
